/**
 * InlineDdlTriggerBody — the embeddable content (parked-statement scan +
 * `TriggerEditFormBody`), consumed directly by the Compact DDL editor's
 * `compactDdlBodyArm`.
 *
 * Mode selection (`create` vs `alter`) is driven by the parked statement's
 * leading keyword:
 *   - CREATE TRIGGER … → create mode (full timing + events + body form)
 *   - ALTER TRIGGER …  → alter mode (ENABLE / DISABLE)
 *
 * Engine gating per DSE2 §3.D6 / Slice D6:
 *   - mssql: BEFORE not allowed (validator + form gate); multi-event OK.
 *   - pgsql: full timing surface; body lives in pgsqlExecuteFunction.
 *   - mysql: single event only; no INSTEAD OF; no ALTER TRIGGER.
 *   - oracle: full timing surface; ALTER no target.
 *   - sqlite: single event only; no ALTER TRIGGER.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI.
 */
import { useMemo } from 'react';

import type { VqeDdlEditContext } from '../edit/editContext';
import {
  TriggerEditFormBody,
  type TriggerFormValues,
} from '../render/common/TriggerEditFormBody';
import { valuesFromParsed } from '../render/common/triggerFormHelpers';
import {
  extractParkedTrigger,
  type ParsedParkedTrigger,
} from '../../../sql-resolver/ddl/extract/trigger';

export interface InlineDdlTriggerBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-trigger (skips the scan). */
  readonly parsedOverride?: ParsedParkedTrigger | null;
  /** Invoked after a successful commit or cancel, so an embedding popover can close. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

export function InlineDdlTriggerBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlTriggerBodyProps) {
  const parsed = useMemo<ParsedParkedTrigger | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      return extractParkedTrigger(buf, stmt);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);

  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';
  const initialValues: Partial<TriggerFormValues> | undefined = parsed
    ? valuesFromParsed(parsed)
    : undefined;

  return (
    <TriggerEditFormBody
      editContext={editContext}
      mode={mode}
      initialValues={initialValues}
      testId="inline-ddl-trigger-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
